import { useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Archive, Copy, KanbanSquare, List, Plus, RefreshCw, Truck } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import { usePickupList, usePickupStats, useTransitionPickup } from '../api/queries'
import type { PickupSummary, Stage } from '../api/types'
import {
  BOARD_STAGES,
  STAGE_LABELS,
  STAGE_SHORT_LABELS,
  STAGE_VARIANT,
} from '../utils/stage-meta'
import { MaterialPickupKanban } from '../components/MaterialPickupKanban'
import { NewPickupDrawer } from '../components/NewPickupDrawer'
import { PickupActionsMenu } from '../components/PickupActionsMenu'

type ViewMode = 'table' | 'kanban'

const VIEW_OPTIONS = [
  { value: 'table' as const, label: 'Table', icon: List },
  { value: 'kanban' as const, label: 'Kanban', icon: KanbanSquare },
]

type StageFilter = 'all' | Stage

const STAGE_FILTER_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'All stages' },
  ...BOARD_STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] })),
]

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function MaterialPickupPage() {
  const navigate = useNavigate()
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [view, setView] = useState<ViewMode>('kanban')
  const [showArchived, setShowArchived] = useState(false)
  const [showDeleted, setShowDeleted] = useState(false)
  const [newPickupOpen, setNewPickupOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const openPickup = (id: string) =>
    navigate({ to: '/material-pickup/$pickupId', params: { pickupId: id } })

  const stats = usePickupStats()
  const list = usePickupList({
    ...(stageFilter === 'all' ? {} : { status: stageFilter }),
    archived: showArchived,
    includeDeleted: showDeleted,
  })
  const transition = useTransitionPickup()

  // include_deleted is additive on the backend (adds duplicates back into the
  // pool); the "Duplicates" toggle is meant to work like "Archived" — an
  // exclusive view — so filter down to just the duplicates client-side.
  const items = showDeleted
    ? (list.data?.items ?? []).filter((r) => r.deleted_at !== null)
    : (list.data?.items ?? [])
  const filtered = searchTerm.trim()
    ? items.filter((row) => {
        const needle = searchTerm.trim().toLowerCase()
        return (
          (row.vendor_name?.toLowerCase().includes(needle)) ||
          row.odoo_po_names.some((n) => n.toLowerCase().includes(needle)) ||
          (row.carrier_name?.toLowerCase().includes(needle))
        )
      })
    : items

  const newCount = stats.data?.new ?? 0
  const scheduledCount = stats.data?.scheduled ?? 0
  const inTransitCount = stats.data?.in_transit ?? 0
  const overdueCount = stats.data?.overdue ?? 0

  const columns = usePickupColumns()

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="Material Pickup Inbox"
        description="Command & control for inbound vendor shipments — what CARLA has staged and what needs the team's attention."
      >
        <div className="ml-auto">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="New requests" value={String(newCount)} />
            <MetricCard variant="inline" label="Scheduled" value={String(scheduledCount)} />
            <MetricCard variant="inline" label="In transit" value={String(inTransitCount)} />
            <MetricCard
              variant="inline"
              label="Overdue"
              value={String(overdueCount)}
            />
          </MetricStrip>
        </div>
      </PageHeaderBand>

      <Toolbar>
        <SearchInput
          ref={searchInputRef}
          containerClassName="flex-1 max-w-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search vendor, PO #, carrier…"
        />
      </Toolbar>

      <Toolbar>
        <ToolbarLeft>
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            aria-label="Filter by stage"
          >
            {STAGE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            variant={showArchived ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setShowArchived((v) => !v)}
            aria-pressed={showArchived}
          >
            <Archive size={14} className="mr-1.5" />
            {showArchived ? 'Showing archived' : 'Archived'}
          </Button>
          <Button
            variant={showDeleted ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setShowDeleted((v) => !v)}
            aria-pressed={showDeleted}
          >
            <Copy size={14} className="mr-1.5" />
            {showDeleted ? 'Showing duplicates' : 'Duplicates'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <Button variant="primary" size="sm" onClick={() => setNewPickupOpen(true)}>
            <Plus size={14} className="mr-1.5" />
            New pickup
          </Button>
          <ViewToggle<ViewMode>
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            aria-label="View mode"
          />
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {list.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="m-0 text-sm text-tertiary">Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Truck}
            title="No pickup requests here yet"
            description={
              stageFilter === 'all'
                ? 'Vendor pickup requests from purchasing@siliconesplus.com will appear here automatically — CARLA extracts the details and alerts Ryan. Didn\'t come in by email? Add it with New pickup above.'
                : `No pickups match the "${STAGE_FILTER_OPTIONS.find((o) => o.value === stageFilter)?.label ?? stageFilter}" filter.`
            }
          />
        ) : view === 'kanban' && !showArchived && !showDeleted ? (
          <MaterialPickupKanban
            items={filtered}
            onSelect={(pickup) => openPickup(pickup.id)}
            onTransition={(pickup, target) => {
              transition.mutate(
                { pickupId: pickup.id, targetStage: target },
                {
                  onError: (err) => {
                    const msg = err instanceof Error ? err.message : 'transition failed'
                    toast.error(msg, { duration: 5000 })
                  },
                },
              )
            }}
          />
        ) : (
          <DataTable
            data={filtered}
            columns={columns}
            loading={list.isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => openPickup(r.id)}
            moduleId="material-pickup"
            maxHeight="100%"
            columnWidths={{
              vendor: 200,
              po_name: 140,
              proposed_date: 120,
              carrier: 140,
              location: 160,
              stage: 130,
              extraction: 110,
              created_at: 110,
              actions: 48,
            }}
            minTableWidth={1100}
            emptyState={
              <EmptyState
                variant="no-match"
                title="No matches"
                description="Try clearing the stage filter or your search."
              />
            }
          />
        )}
      </div>

      <NewPickupDrawer
        open={newPickupOpen}
        onOpenChange={setNewPickupOpen}
        onCreated={openPickup}
      />
    </div>
  )
}

function usePickupColumns(): ColumnDef<PickupSummary, unknown>[] {
  return useMemo<ColumnDef<PickupSummary, unknown>[]>(() => [
    {
      id: 'vendor',
      header: 'Vendor',
      accessorFn: (r) => r.vendor_name ?? '',
      cell: (ctx) =>
        ctx.row.original.vendor_name ?? (
          <em className="text-tertiary">unknown</em>
        ),
    },
    {
      id: 'po_name',
      header: 'PO #',
      accessorFn: (r) => r.odoo_po_names.join(', '),
      cell: (ctx) => (
        <span className="text-secondary">
          {ctx.row.original.odoo_po_names.length ? ctx.row.original.odoo_po_names.join(', ') : '—'}
        </span>
      ),
    },
    {
      id: 'proposed_date',
      header: 'Proposed date',
      accessorFn: (r) => r.proposed_pickup_date ?? '',
      cell: (ctx) => (
        <span className="text-tertiary">
          {formatDate(ctx.row.original.proposed_pickup_date)}
        </span>
      ),
    },
    {
      id: 'carrier',
      header: 'Carrier',
      accessorFn: (r) => r.carrier_name ?? '',
      cell: (ctx) => (
        <span className="text-secondary">{ctx.row.original.carrier_name ?? '—'}</span>
      ),
    },
    {
      id: 'location',
      header: 'Location',
      accessorFn: (r) => r.pickup_location ?? '',
      cell: (ctx) => (
        <span className="text-secondary">{ctx.row.original.pickup_location ?? '—'}</span>
      ),
    },
    {
      id: 'stage',
      header: 'Stage',
      accessorFn: (r) => STAGE_SHORT_LABELS[r.status],
      cell: (ctx) =>
        ctx.row.original.deleted_at ? (
          <Chip size="xs" variant="neutral">Duplicate</Chip>
        ) : (
          <Chip size="xs" variant={STAGE_VARIANT[ctx.row.original.status]} dot>
            {STAGE_SHORT_LABELS[ctx.row.original.status]}
          </Chip>
        ),
    },
    {
      id: 'extraction',
      header: 'Extraction',
      accessorFn: (r) => r.extraction_state,
      cell: (ctx) => {
        const s = ctx.row.original.extraction_state
        const variant =
          s === 'complete' ? 'success' : s === 'failed' ? 'danger' : 'neutral'
        const label =
          s === 'complete'
            ? 'Complete'
            : s === 'failed'
              ? 'Failed'
              : s === 'running'
                ? 'Running…'
                : 'Queued'
        return (
          <Chip size="xs" variant={variant}>
            {label}
          </Chip>
        )
      },
    },
    {
      id: 'created_at',
      header: 'Received',
      accessorKey: 'created_at',
      cell: (ctx) => (
        <span className="text-tertiary">{formatDate(ctx.row.original.created_at)}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (ctx) => <PickupActionsMenu row={ctx.row.original} />,
    },
  ], [])
}
